import type { ChatDms, ChatMessageView, ChatReaction, ChatRoomView } from '@wahala/chat';
import type { ApiPaths } from './api-paths';
import type { Campaign } from './campaign';

// Our http api (same origin, /api/v1). Tokens live in memory and sessionStorage only: cookies are
// blocked in the Lagos Life frame and storage there can be purged at any time.

export interface Me {
  id: string;
  /** The name everybody sees: the nickname when one is set. */
  name: string;
  nickname?: string | null;
}
export interface ApiConfig {
  mode: 'test' | 'live';
  devAuth: boolean;
  lagosLife: boolean;
  protocol: number;
  presets: ParkPreset[];
  /** Every game type with the park office's switches (absent from an older server: the presets, all on). */
  gameTypes?: Array<{ id: string; on: boolean; solo: boolean; stake: boolean }>;
  /** What buying the weather costs: the next round, the park session. */
  weatherPrices?: { round: number; session: number };
  perkPrices?: { name: number; theme: number };
  /** The fares a stake table may take (naira of the world's game money, never coins). */
  stakeTiers?: number[];
  /** Error reports (OP-06): sent through our own /api/v1/errors, from this share of phones. */
  errors?: { dsn: string; sampleRate: number; environment: string };
  /**
   * What this server offers (PF-23): Lagos Life inside its frame, and/or our own standalone play;
   * and from the platform ports (PF-18) whether coin packs and stake tables exist here at all.
   */
  platforms?: { lagosLife: boolean; standalone: boolean; coinPacks?: boolean; stakes?: boolean };
  /** How the game runs on phones (FE-28): the share of phones that report it. */
  telemetry?: { sampleRate: number };
  /** Saving a website profile (PF-19): which kinds of contact can get a code here (standalone only). */
  accounts?: { email: boolean; phone: boolean };
  /** Web push (FE-51): the key browsers subscribe with; absent, no push here and the phone never asks. */
  push?: { publicKey: string };
  /** COMPETE AND WIN CASH (standalone only): on or not, the window, the runs, the prizes in whole naira. */
  compete?: CompeteInfo;
  /** The community chat (our website's players): its button shows while the park office has it on. */
  chat?: { on: boolean };
}

export interface CompeteInfo {
  on: boolean;
  /** Lagos time: the weekday (6 Saturday) and minutes after midnight. */
  window: { weekday: number; opens: number; lastStart: number; closes: number };
  runsPerGame: number;
  heatEveryMin: number;
  games: Record<'grandprix' | 'hustle', { on: boolean; prizes: number[] }>;
  /** The Saturday's Track of the Week (a race ring's map id), once the day is written. */
  track?: string;
}

/** A game's Saturday board: best run each, fastest first. Scores are ms (Grand Prix). */
export interface CompeteBoard {
  day: string;
  game: string;
  players: number;
  rows: Array<{ place: number; name: string; score: number; me?: true }>;
  me: { place: number; name: string; score: number; me: true; gapTo3rd: number | null } | null;
}

/** This Saturday's runs for me: what each game has left, my best time and each run so far. */
export interface CompeteRuns {
  day: string;
  open: boolean;
  canStart: boolean;
  games: Record<
    'grandprix' | 'hustle',
    {
      on: boolean;
      left: number;
      /** Grand Prix: the best time in ms (null: no finished run yet). */
      best: number | null;
      runs: Array<{ slot: number; status: string; score: number | null }>;
    }
  >;
  /** A run of mine still going (the page was left or lost): Resume takes me back into it. */
  going?: { game: 'grandprix' | 'hustle'; kind: string } | null;
}

export interface CompetePrizes {
  day: string;
  games: Record<'grandprix' | 'hustle', { on: boolean; prizes: number[]; total: number }>;
  /** Over the games that are on. */
  total: number;
  /** The park office paused new runs (runs going finish). */
  paused: boolean;
  /** The park office cancelled the day: no runs, no prizes. */
  cancelled?: boolean;
  /** What the office tells players about the day (why it is paused, why the close moved). */
  notice: string | null;
  /** The close moved this much after an outage. */
  extendedMs: number;
  /** The office's announcement for the hub. */
  message: string | null;
  track?: string;
  weather?: string;
}

/** One of my removed runs and its appeal (or the time left to make one). */
export interface CompeteRemoval {
  runId: string;
  day: string;
  game: string;
  reason: string | null;
  appealUntil: number;
  mayAppeal: boolean;
  appeal: { state: string; text: string; reply: string | null; decidedAt: number | null } | null;
}

export interface CompeteRules {
  version: number;
  rules: Array<{ title: string; text: string }>;
  agreed: boolean;
  /** They agreed to an older version: the rule book says the rules changed. */
  changed?: boolean;
}

/** Whether this player may register, and their registration (hints only) if they have one. */
export type CompeteMe =
  | {
      registered: false;
      may: 'register' | 'save_profile_first' | 'website_only';
      /** The saved profile's nickname: the sign-up uses it and doesn't ask again. */
      nickname?: string | null;
      /** The email the profile was saved with (a hint): the sign-up uses it and doesn't ask again. */
      email?: string | null;
      rules?: { version: number; agreed: boolean };
    }
  | {
      registered: true;
      status: 'active' | 'suspended' | 'barred';
      nickname: string | null;
      state: string;
      bank: { name: string; last4: string };
      /** Only if they gave one (sign-up asks for the email only). */
      phone: string | null;
      email: string;
      bankChangeFrom: string;
      rules?: { version: number; agreed: boolean };
    };

export interface CompeteSignUp {
  fullName: string;
  nickname: string;
  birthDate: string;
  state: string;
  /** Left out when the profile was saved with an email: that one is used. */
  email?: string;
  /** Optional: sign-up proves the email only. */
  phone?: string;
  bankCode: string;
  accountNumber: string;
}
/** The kinds of push a player turns on one by one (FE-51). */
export type PushKind = 'calls' | 'park' | 'today' | 'rankings' | 'news';
export type PushStep = 'shown' | 'yes' | 'later' | 'granted' | 'denied' | 'subscribed';
export interface PushSubscriptionState {
  subscribed: boolean;
  kinds: PushKind[];
}
/** A website account (PF-19): a guest, a saved profile, or a distributor's player (never asked). */
export type AccountKind = 'guest' | 'website' | 'platform';
export type ContactKind = 'email' | 'phone';
export interface AccountState {
  account: AccountKind;
  kinds: { email: boolean; phone: boolean };
  saved?: { kind: ContactKind; hint: string };
}
export interface CodeSent {
  sent: true;
  kind: ContactKind;
  hint: string;
  expiresIn: number;
}
export interface Device {
  id: string;
  label: string;
  since: string;
  lastSeen: string;
  current: boolean;
}
export interface Session {
  token: string;
  user: Me;
  mode: 'test' | 'live';
}
export interface ShopItem {
  id: string;
  kind: ShopKind;
  item: string;
  name: string;
  price: number;
  owned: boolean;
  /** In use now (every kind but stickers). */
  equipped: boolean;
  /** Last day on sale, when the item has a window. */
  until?: string;
}
export type Theme = 'day' | 'night' | 'rain' | 'harmattan';
/** What the weather sheet sells: night comes by itself every round, so it is not a pick. */
export type BuyableWeather = Exclude<Theme, 'night'>;
/** Lifetime totals from rounds the server applied; `score` is what goes to the Lagos Life board. */
export interface PlayerStats {
  rounds: number;
  wins: number;
  stops: number;
  knocks: number;
  reports: number;
  rightVotes: number;
  score: number;
}

/** The leave rules as the api counts them for me (GET /me/leave): server ms throughout. */
export interface LeaveStanding {
  marks: number;
  /** Left of my wait now (0: none). */
  waitMs: number;
  until: number | null;
  /** The wait my next leave would start (0: it would be a warning only). */
  nextWaitMs: number;
  windowMs: number;
}

/** A stake table's money as Lagos Life settled it (pot, payout and cut are null until it has). */
export interface StakeLine {
  stake: number;
  seats: number;
  status: string;
  pot: number | null;
  payout: number | null;
  cut: number | null;
  draw: boolean;
  winnerName: string | null;
  me: boolean;
}

/** What the round just played paid this player (recorded: false until the server has it). */
export type LastRound =
  | { recorded: false }
  | {
      recorded: true;
      coins: { earned: number; reason: 'win' | 'reach' | 'played'; capped: boolean } | null;
      stake: StakeLine | null;
      /** This round's move on the overall rankings, once the server noted it (absent from an older server). */
      rank?: RankMove | null;
    };

/** The boards (the rankings spec): overall, and one per game type. */
export type RankGame = 'all' | 'park' | 'race' | 'hide' | 'powers';
export type TierId = 'fresh' | 'conductor' | 'driver' | 'correct' | 'oga' | 'boss' | 'chairman' | 'top';
/** A round's points and the overall rank before and after it (rank null: not on the board before). */
export interface RankMove {
  points: number;
  before: { points: number; rank: number | null };
  after: { points: number; rank: number };
}
export interface RankRow {
  rank: number;
  name: string;
  points: number;
  tier: TierId;
  me?: true;
}
export interface Rankings {
  game: RankGame;
  total: number;
  top: RankRow[];
  me: (RankRow & { percent: number; total: number }) | null;
  around: RankRow[];
}
export interface RankStanding {
  rank: number;
  total: number;
  percent: number;
}
export interface MoneyLine {
  rounds: number;
  staked: number;
  won: number;
}
export interface RankProfile {
  name: string;
  points: number;
  tier: { tier: TierId; level: number; next: { tier: TierId; at: number } | null; progress: number };
  rank: RankStanding | null;
  totals: { rounds: number; finished: number; wins: number; coins: number };
  /** fare: Lagos Life players (null on the website); prize: website players (null inside Lagos Life). */
  money: { fare: MoneyLine | null; prize: MoneyLine | null };
  games: Array<{
    game: Exclude<RankGame, 'all'>;
    rounds: number;
    finished: number;
    wins: number;
    solo: number;
    contributions: {
      stops: number;
      knocks: number;
      reports: number;
      rightVotes: number;
      raceFinishes: number;
      racePodiums: number;
      raceBeaten: number;
    };
    coins: number;
    fares: number;
    fare: MoneyLine;
    points: number;
    rank: RankStanding | null;
  }>;
}

export interface RoundHistory {
  rounds: Array<{
    matchId: string;
    park: string;
    endedAt: string;
    preset: string | null;
    role: 'crew' | 'impostor';
    won: boolean;
    finished: boolean;
    stops: number;
    coins: number;
    stake: StakeLine | null;
  }>;
  totals: { rounds: number; wins: number; coins: number; fareWon: number };
}

export type ShopKind = 'load' | 'slogan' | 'finish' | 'horn' | 'owa' | 'sticker' | 'power';
export type Slot = Exclude<ShopKind, 'sticker'>;
export interface ShopState {
  coins: number;
  debt: number;
  items: ShopItem[];
  equipped: Partial<Record<Slot, string>>;
  tickets: number;
  ticketPrice: number;
  /** The player's own store's packs: coins, and the naira price that store charges. */
  packs: Array<{ productId: string; coins: number; price: number }>;
  packsOn: boolean;
  /** Where the packs are paid for, when they are on: Lagos Life's sheet, or our website's (Stig Labs pay). */
  checkout?: 'lagoslife' | 'website';
}
export type PassReward = { coins: number } | { item: { id: string; kind: ShopKind; item: string; name: string } };
export interface PassCell {
  reward: PassReward | null;
  claimed: boolean;
}
export interface PassState {
  season: {
    id: string;
    name: string;
    startsAt: string;
    endsAt: string;
    running: boolean;
    premiumPrice: number;
  } | null;
  xp?: number;
  tier?: number;
  premium?: boolean;
  coins?: number;
  tiers?: Array<{ tier: number; xp: number; free: PassCell | null; premium: PassCell | null }>;
}
/** A player's level, achievements and cosmetics (BE-24): read only, from the server's records. */
export interface ProfileState {
  id: string;
  name: string;
  nickname: string | null;
  xp: number;
  level: number;
  /** XP into the current level, and what the next one costs (0 at the top). */
  into: number;
  next: number;
  stats: { rounds: number; wins: number; stops: number; rightVotes: number };
  achievements: Array<{
    id: string;
    name: string;
    text: string;
    done: boolean;
    at?: string;
    reward?: { id: string; kind: ShopKind; item: string; name: string };
  }>;
  items: Array<ShopItem & { source: 'starter' | 'coins' | 'grant' | 'pass' }>;
  equipped: Partial<Record<Slot, string>>;
}
/** Someone you finished a round with: our own per-game id and the name they play under. */
export interface RecentPlayer {
  id: string;
  name: string;
  lastAt: string;
}
export interface PartyState {
  party: {
    code: string;
    leader: boolean;
    leaderName: string;
    /** The park the leader took the party to; members follow into their held seat. */
    park: string | null;
    max: number;
    members: Array<{ id: string; name: string; leader: boolean; me: boolean }>;
  } | null;
}
export interface Invite {
  party: string;
  from: string;
  at: number;
}
export interface ChallengeState {
  day: string;
  resetsAt: string;
  swapPrice: number;
  swapUsed: boolean;
  challenges: Array<{
    slot: number;
    id: string;
    text: string;
    target: number;
    progress: number;
    coins: number;
    done: boolean;
  }>;
}
export interface Claimed {
  granted: number;
  coins: number;
  debt: number;
  delivered: boolean;
}
/** A website coin-pack purchase, as Stig Labs pay's record has it. */
export interface WebsitePurchase {
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  granted: number;
  coins?: number;
  debt?: number;
}
export interface JoinTicket {
  code: string;
  roomId: string;
  workerUrl: string;
  ticket: string;
  /** A stake park's Lagos Life match: it is rejoined through stakes/bind, never by its code. */
  vatarMatch?: string /** The office's first-play park money, paid as this player's first game starts (promos). */;
  welcome?: { coins: number; passPremium: boolean };
}

/** A coupon code redeemed: what it gave and the wallet after. */
export interface Redeemed {
  coins: number;
  passPremium: boolean;
  name: string;
  balance: number;
}

export interface OpenPark {
  code: string;
  count: number;
  capacity: number;
  minPlayers: number;
  preset: string;
  name?: string;
  theme?: string;
}

/** A stake table still filling: a Lagos Life match with seats left, joined by its code there. */
export interface StakeTable {
  matchId: string;
  code: string;
  stake: number;
  seats: number;
  seated: number;
  /** The game it plays (2 seats Race, 4 The Park); an older api leaves it out. */
  game?: 'park' | 'race';
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

const KEY = 'wahala.session';
let current: Session | null = null;

export function loadSession(): Session | null {
  if (current) return current;
  try {
    const raw = sessionStorage.getItem(KEY);
    current = raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    current = null;
  }
  return current;
}

export function saveSession(s: Session | null): void {
  current = s;
  try {
    if (s) sessionStorage.setItem(KEY, JSON.stringify(s));
    else sessionStorage.removeItem(KEY);
  } catch {
    // storage blocked in the frame: memory only
  }
}

// ------------------------------------------------------------ typed against the api's routes
// api-paths.d.ts is generated from the api's OpenAPI spec (`pnpm --filter @wahala/api openapi`), so a
// renamed route, a removed one or a changed body breaks this file's typecheck.

type Verb = 'GET' | 'POST';
/** A spec path as the client writes it: `/api/v1/parks/{code}/join` is `/parks/${string}/join`. */
type Fill<S extends string> = S extends `${infer A}{${string}}${infer B}` ? `${A}${string}${Fill<B>}` : S;
/** Each route this verb has: the url the client writes and the spec's operation. */
type Route<V extends Verb> = {
  [K in keyof ApiPaths]: K extends `/api/v1${infer R}`
    ? Lowercase<V> extends keyof ApiPaths[K]
      ? { url: Fill<R>; op: ApiPaths[K][Lowercase<V> & keyof ApiPaths[K]] }
      : never
    : never;
}[keyof ApiPaths];
/** The urls a client may call with this verb (a query string only where the route reads one). */
export type ApiUrl<V extends Verb> =
  Route<V> extends infer R
    ? R extends { url: infer U extends string; op: infer O }
      ? O extends { query: object }
        ? `${U}?${string}`
        : U
      : never
    : never;
type OpOf<V extends Verb, S extends string> =
  Route<V> extends infer R
    ? R extends { url: infer U extends string; op: infer O }
      ? S extends U | `${U}?${string}`
        ? O
        : never
      : never
    : never;
/** The body a route takes: required, optional, or none at all (an empty object is still fine). */
export type ApiBody<O> = 'body' extends keyof O
  ? object extends Pick<O, 'body' & keyof O>
    ? [body?: Exclude<O['body' & keyof O], undefined>]
    : [body: O['body' & keyof O]]
  : [body?: Record<string, never>];

/** A JSON body some POST route takes. */
export type ApiBodyOf<S extends ApiUrl<'POST'>> = NonNullable<ApiBody<OpOf<'POST', S>>[0]>;
/** The presets a park can be opened with (the server's /config lists the ones on offer). */
export type ParkPreset = NonNullable<ApiBodyOf<'/parks'>['preset']>;

/**
 * The world Lagos Life said this phone plays in (PF-03), from Vatar.init: its sandbox frames our live
 * address with test sessions, so the api needs telling which world a call is for until it holds a
 * token (which says so itself). The api checks it; this only picks where to ask.
 */
let platformMode: 'test' | 'live' | null = null;
export function setPlatformMode(mode: 'test' | 'live'): void {
  platformMode = mode;
}

/**
 * One call to our api, its url and body checked against the route (one overload per verb, so the
 * url keeps its literal type). The answer's type comes from the caller's declared return type.
 */
function call<T, S extends ApiUrl<'GET'>>(method: 'GET', path: S): Promise<T>;
function call<T, S extends ApiUrl<'POST'>>(method: 'POST', path: S, ...body: ApiBody<OpOf<'POST', S>>): Promise<T>;
async function call<T>(method: Verb, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const s = loadSession();
  if (s) headers.authorization = `Bearer ${s.token}`;
  const mode = s?.mode ?? platformMode;
  if (mode) headers['x-wahala-mode'] = mode;
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new ApiError(0, 'network');
  }
  if (res.status === 204) return {} as T;
  const json = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) throw new ApiError(res.status, String(json?.error ?? 'generic'), json ?? {});
  // a 200 that isn't our JSON (a page from the proxy while the api restarts) is a failure, never an
  // empty answer the screens would read as data
  if (json === null) throw new ApiError(res.status, 'generic');
  return json as T;
}

/** Waits between sign-in tries while Lagos Life's call budget is spent (T-36): about 7 s in all. */
export const BUSY_WAITS_MS = [1_000, 2_000, 4_000];

/**
 * Lagos Life's 600 calls a minute are shared by every player: when they run out the api answers
 * 503 try_again rather than hold the request. A sign-in queues here instead, on the phone, a few
 * seconds at most; anything else is thrown at once.
 */
export async function whenNotBusy<T>(f: () => Promise<T>, waits = BUSY_WAITS_MS): Promise<T> {
  for (let i = 0; ; i++) {
    try {
      return await f();
    } catch (e) {
      const busy = e instanceof ApiError && e.status === 503 && e.code === 'try_again';
      const wait = waits[i];
      if (!busy || wait === undefined) throw e;
      await new Promise((r) => setTimeout(r, wait));
    }
  }
}

/** Where this player stands in the community chat (GET /chat/me). */
export type ChatMe =
  | { may: 'save_profile_first' | 'pick_nickname' | 'website_only' | 'off' }
  | {
      may: 'chat';
      name: string;
      muted: { until: number | null; reason: string } | null;
      dms: ChatDms;
      blocked: string[];
      live: { url: string } | null;
    };
export interface ChatPage {
  room: Omit<ChatRoomView, 'unread' | 'last'>;
  messages: ChatMessageView[];
  more: boolean;
  /** My own reactions, by message. */
  mine: Record<string, ChatReaction[]>;
  /** Ids of my own messages on the page. */
  own: string[];
}

export const api = {
  chatMe: (): Promise<ChatMe> => call('GET', '/chat/me'),
  chatToken: (): Promise<{ token: string; url: string; inbox: string }> => call('POST', '/chat/token'),
  chatLists: (): Promise<{ rooms: ChatRoomView[]; dms: ChatRoomView[] }> => call('GET', '/chat/lists'),
  chatUnread: (): Promise<{ rooms: number; dms: number }> => call('GET', '/chat/unread'),
  chatHistory: (roomId: string, before?: string): Promise<ChatPage> =>
    call('GET', `/chat/rooms/${roomId}/messages?${before ? `before=${before}` : ''}`),
  chatSend: (roomId: string, body: string, replyTo?: string | null): Promise<{ message: ChatMessageView }> =>
    call('POST', `/chat/rooms/${roomId}/messages`, { body, ...(replyTo ? { replyTo } : {}) }),
  chatRead: (roomId: string, last: string): Promise<{ ok: true }> =>
    call('POST', `/chat/rooms/${roomId}/read`, { last }),
  chatTyping: (roomId: string): Promise<{ ok: true }> => call('POST', `/chat/rooms/${roomId}/typing`),
  chatDelete: (id: string): Promise<{ ok: true }> => call('POST', `/chat/messages/${id}/delete`),
  chatReact: (id: string, kind: ChatReaction, on: boolean): Promise<{ reactions: ChatMessageView['reactions'] }> =>
    call('POST', `/chat/messages/${id}/react`, { kind, on }),
  chatReport: (id: string, reason: 'abuse' | 'spam' | 'scam' | 'other'): Promise<{ ok: true; already: boolean }> =>
    call('POST', `/chat/messages/${id}/report`, { reason }),
  chatDm: (name: string): Promise<{ room: ChatRoomView }> => call('POST', '/chat/dm', { name }),
  chatPeople: (q: string): Promise<{ names: string[] }> => call('GET', `/chat/people?q=${encodeURIComponent(q)}`),
  chatBlock: (name: string, on: boolean): Promise<{ blocked: string[] }> => call('POST', '/chat/block', { name, on }),
  chatSettings: (dms: ChatDms): Promise<{ dms: ChatDms }> => call('POST', '/chat/settings', { dms }),
  config: (): Promise<ApiConfig> => call('GET', '/config'),
  pushSubscribe: (body: {
    endpoint: string;
    keys: { p256dh: string; auth: string };
    installed: boolean;
    kinds?: PushKind[];
    fallback?: PushKind[];
  }): Promise<PushSubscriptionState> => call('POST', '/push/subscribe', body),
  pushState: (endpoint: string): Promise<PushSubscriptionState> => call('POST', '/push/state', { endpoint }),
  pushKinds: (endpoint: string, kinds: PushKind[]): Promise<PushSubscriptionState> =>
    call('POST', '/push/kinds', { endpoint, kinds }),
  pushUnsubscribe: (endpoint: string): Promise<{ removed: boolean }> => call('POST', '/push/unsubscribe', { endpoint }),
  pushTest: (endpoint?: string): Promise<{ queued: number }> =>
    call('POST', '/push/test', endpoint ? { endpoint } : {}),
  pushFunnel: (step: PushStep): Promise<{ ok: boolean }> => call('POST', '/push/funnel', { step }),
  exchange: (session: string): Promise<Session> => whenNotBusy(() => call('POST', '/auth/exchange', { session })),
  devLogin: (name: string): Promise<Session> => call('POST', '/auth/dev', { name }),
  /** Standalone (PF-19): a new guest, and the key this phone keeps to come back. */
  /** Standalone: a new guest, with the ad click (lib/ad.ts) and campaign tags (lib/campaign.ts) it came from. */
  guest: (name: string, adRef?: string | null, campaign?: Campaign | null): Promise<Session & { guestKey: string }> =>
    call('POST', '/auth/guest', { name, ...(adRef ? { adRef } : {}), ...(campaign ? { campaign } : {}) }),
  /** Standalone: this phone landed from an ad (lib/ad.ts); the ref it keeps for its new guest. */
  adClick: (click: { network: 'propellerads'; clickId: string } & Record<string, string>): Promise<{ ref: string }> =>
    call('POST', '/ads/click', click),
  guestResume: (guestKey: string): Promise<Session> => call('POST', '/auth/guest/resume', { guestKey }),
  /** Standalone (PF-24): our own board, the same shape as Lagos Life's. */
  leaderboard: (): Promise<{
    top: Array<{ rank: number; name: string; score: number }>;
    me?: { rank: number; name: string; score: number };
  }> => call('GET', '/leaderboard'),
  me: (): Promise<Me & { park: string | null; tutorialDone?: boolean; account?: AccountKind }> => call('GET', '/me'),
  /** Saved website profiles (PF-19): never called inside a distributor's frame. */
  account: (): Promise<AccountState> => call('GET', '/account'),
  saveStart: (kind: ContactKind, contact: string): Promise<CodeSent> =>
    call('POST', '/account/save/start', { kind, contact }),
  saveFinish: (code: string, pin: string): Promise<{ saved: true; kind: ContactKind; hint: string }> =>
    call('POST', '/account/save/finish', { code, pin }),
  signIn: (kind: ContactKind, contact: string, pin: string): Promise<Session & { guestKey: string }> =>
    call('POST', '/account/signin', { kind, contact, pin }),
  resetStart: (kind: ContactKind, contact: string): Promise<CodeSent> =>
    call('POST', '/account/reset/start', { kind, contact }),
  resetFinish: (
    kind: ContactKind,
    contact: string,
    code: string,
    pin: string,
  ): Promise<Session & { guestKey: string }> => call('POST', '/account/reset/finish', { kind, contact, code, pin }),
  devices: (): Promise<{ devices: Device[] }> => call('GET', '/account/devices'),
  signOutOthers: (): Promise<Session & { removed: number; guestKey?: string }> =>
    call('POST', '/account/devices/signout-others', {}),
  deleteAccount: (body: { pin: string } | { confirm: true }): Promise<{ deleted: true }> =>
    call('POST', '/account/delete', body),
  /** COMPETE AND WIN CASH (standalone only, never inside a distributor's frame). */
  competeMe: (): Promise<CompeteMe> => call('GET', '/compete/me'),
  competeBanks: (): Promise<{ banks: Array<{ code: string; name: string }> }> => call('GET', '/compete/banks'),
  competeRules: (): Promise<CompeteRules> => call('GET', '/compete/rules'),
  competeRuns: (): Promise<CompeteRuns> => call('GET', '/compete/runs'),
  /** Staging only: the messages recorded for an email (codes are recorded there, not sent). */
  testOutbox: (email: string): Promise<{ messages: Array<{ subject: string; text: string; at: number }> }> =>
    call('GET', `/test/outbox?kind=email&to=${encodeURIComponent(email)}`),
  competeWinners: (
    game: 'grandprix' | 'hustle',
  ): Promise<{
    game: string;
    saturdays: Array<{
      day: string;
      winners: Array<{ place: number; name: string; score: number; prize: number; checked: boolean }>;
    }>;
  }> => call('GET', `/compete/winners?game=${game}`),
  competeEarners: (): Promise<{ earners: Array<{ place: number; name: string; won: number; prizes: number }> }> =>
    call('GET', '/compete/earners'),
  competeStats: (): Promise<{
    runs: number;
    finished: number;
    best: number | null;
    saturdays: number;
    top3: number;
    wins: number;
    won: number;
    recent: Array<{ day: string; game: string; kind: string; status: string; score: number | null }>;
  }> => call('GET', '/compete/stats'),
  /** Another player's public numbers, by the nickname the boards show. */
  competePlayer: (
    name: string,
  ): Promise<{
    name: string;
    runs: number;
    finished: number;
    best: number | null;
    saturdays: number;
    top3: number;
    wins: number;
    won: number;
  }> => call('GET', `/compete/players?name=${encodeURIComponent(name)}`),
  /** My removed runs of the last 30 days, with their appeals. */
  competeAppeals: (): Promise<{ removals: CompeteRemoval[] }> => call('GET', '/compete/appeals'),
  /** Appeal a removed run of mine (once, within 7 days of the removal). */
  competeAppeal: (runId: string, text: string): Promise<{ id: string }> =>
    call('POST', '/compete/appeals', { runId, text }),
  /** This Saturday's prizes as the close will pay them (the park office sets them). */
  competePrizes: (): Promise<CompetePrizes> => call('GET', '/compete/prizes'),
  /** A game's board today: the top rows and my own (with my gap to 3rd). */
  competeBoard: (game: 'grandprix' | 'hustle'): Promise<CompeteBoard> => call('GET', `/compete/board?game=${game}`),
  /** The next heat of each game (null: none left today or the game is off). */
  competeHeats: (): Promise<{
    day: string;
    heats: Record<'grandprix' | 'hustle', { startAt: number; lobbyOpensAt: number; seats: number } | null>;
  }> => call('GET', '/compete/heats'),
  /** Take one of my runs in the next heat: a seat in its lobby. */
  competeHeatJoin: (
    game: 'grandprix' | 'hustle',
  ): Promise<JoinTicket & { run: { id: string; game: string; slot: number; left: number } }> =>
    call('POST', '/compete/heats/join', { game }),
  /** Start one of my scored runs: the run is taken now and spent at Go. */
  competeRun: (
    game: 'grandprix' | 'hustle',
  ): Promise<JoinTicket & { run: { id: string; game: string; slot: number; left: number } }> =>
    call('POST', '/compete/runs', { game }),
  /** Back into my run still going: a fresh ticket into its park. */
  competeResume: (): Promise<JoinTicket & { run: { id: string; game: 'grandprix' | 'hustle' } }> =>
    call('POST', '/compete/runs/resume', {}),
  competeAgree: (version: number): Promise<{ version: number; agreed: true }> =>
    call('POST', '/compete/rules/agree', { version }),
  competeStart: (
    b: CompeteSignUp,
  ): Promise<{
    /** No code when the profile's saved email is used: it was proved when the profile was saved. */
    sent: boolean;
    proven: boolean;
    email: string;
    bank: { name: string; last4: string };
    expiresIn: number;
  }> => call('POST', '/compete/register/start', b),
  competeFinish: (emailCode?: string): Promise<CompeteMe> =>
    call('POST', '/compete/register/finish', emailCode ? { emailCode } : {}),
  /** Whether a nickname is free, asked while it is typed. */
  nicknameFree: (name: string): Promise<{ name: string; free: boolean; reason?: string }> =>
    call('GET', `/nickname/free?name=${encodeURIComponent(name)}`),
  /** The tutorial was finished or skipped: don't offer it again. */
  tutorialDone: (): Promise<{ tutorialDone: boolean }> => call('POST', '/me/tutorial', {}),
  /** Wahala is on this phone's home screen (lib/install): once per player and phone. */
  appInstalled: (
    how: 'installed' | 'launched',
    platform: 'android' | 'ios' | 'other',
  ): Promise<{ recorded: boolean }> => call('POST', '/me/app', { how, platform }),
  createPark: (preset: ParkPreset, perks?: { name?: string; theme?: Theme }, open = false): Promise<JoinTicket> =>
    call('POST', '/parks', {
      preset,
      open,
      ...(perks && (perks.name || perks.theme) ? { perks } : {}),
    }),
  /** Open parks a stranger can join: waiting, not full, fullest first. */
  openParks: (): Promise<{ parks: OpenPark[] }> => call('GET', '/parks'),
  /** A seat in the open park closest to starting (of this game type), or a new open park. */
  quickPlay: (preset?: ParkPreset): Promise<JoinTicket> => call('POST', '/parks/quick', preset ? { preset } : {}),
  /** Play alone on the server: a park for one, solo drivers at this level in the other seats. */
  soloPark: (preset: ParkPreset, side: 'driver' | 'agbero' | 'surprise', level: number): Promise<JoinTicket> =>
    call('POST', '/parks/solo', { preset, side, level }),
  /** Buy a park's next round weather (the first buyer wins). */
  buyWeather: (code: string, kind: BuyableWeather): Promise<{ kind: string; price: number }> =>
    call('POST', `/parks/${encodeURIComponent(code)}/weather`, { kind }),
  /** Stake tables still filling (only ever a list; joining goes through Lagos Life). */
  stakes: (): Promise<{ tables: StakeTable[] }> => call('GET', '/stakes'),
  /** Before Lagos Life's sheet takes any fare: our stake ban and daily cap say yes, or refuse with a code. */
  stakeAllowed: (stake: number): Promise<{ ok: true; cap: number; left: number }> =>
    call('GET', `/stakes/allowed?stake=${encodeURIComponent(String(stake))}`),
  /** Our seat in the stake park for a Lagos Life match we are in (the server checks with Lagos Life). */
  bindStake: (matchId: string): Promise<JoinTicket> => call('POST', '/stakes/bind', { matchId }),
  /** A Lagos Life match we can't play (the sandbox's 2-seat test table): once in play, our server draws it. */
  buyTicket: (requestId: string): Promise<{ tickets: number; coins: number }> =>
    call('POST', '/shop/ticket', { requestId }),
  joinPark: (code: string): Promise<JoinTicket> => call('POST', `/parks/${encodeURIComponent(code)}/join`, {}),
  wallet: (): Promise<{ coins: number; debt: number }> => call('GET', '/wallet'),
  redeem: (code: string): Promise<Redeemed> => call('POST', '/promo/redeem', { code }),
  shop: (): Promise<ShopState> => call('GET', '/shop'),
  pass: (): Promise<PassState> => call('GET', '/pass'),
  stats: (): Promise<PlayerStats> => call('GET', '/stats'),
  /** The leave rules: my marks of the day, the wait left and the wait the next leave would start. */
  leaveStanding: (): Promise<LeaveStanding> => call('GET', '/me/leave'),
  lastRound: (park: string): Promise<LastRound> => call('GET', `/rounds/last?park=${encodeURIComponent(park)}`),
  roundHistory: (): Promise<RoundHistory> => call('GET', '/rounds/history'),
  /** A global board: the top, my rank and tier, and the rows around me. */
  rankings: (game: RankGame): Promise<Rankings> =>
    call('GET', `/rankings?game=${encodeURIComponent(game)}&around=me&limit=50`),
  /** My record per game type, what I earned, my points, ranks and tier. */
  rankProfile: (): Promise<RankProfile> => call('GET', '/profile/me'),
  challenges: (): Promise<ChallengeState> => call('GET', '/challenges'),
  swapChallenge: (slot: number): Promise<ChallengeState> => call('POST', '/challenges/swap', { slot }),
  passUnlock: (seasonId: string): Promise<PassState> => call('POST', '/pass/unlock', { seasonId }),
  passClaim: (seasonId: string, track: 'free' | 'premium', tier: number): Promise<PassState> =>
    call('POST', '/pass/claim', { seasonId, track, tier }),
  equip: (body: { itemId: string } | { slot: Slot; itemId: null }): Promise<{ equipped: ShopState['equipped'] }> =>
    call('POST', '/shop/equip', body),
  buy: (itemId: string): Promise<{ itemId: string; owned: boolean; coins: number; charged: number }> =>
    call('POST', '/shop/buy', { itemId }),
  profile: (): Promise<ProfileState> => call('GET', '/profile'),
  /** A fresh session: the nickname rides in the token, so the next park's ticket carries it. */
  setNickname: (nickname: string): Promise<Session> => call('POST', '/me/nickname', { nickname }),
  dropNickname: (): Promise<Session> => call('POST', '/me/nickname/drop', {}),
  recentPlayers: (): Promise<{ players: RecentPlayer[] }> => call('GET', '/players/recent'),
  party: (): Promise<PartyState> => call('GET', '/party'),
  partyStart: (): Promise<PartyState> => call('POST', '/party', {}),
  partyJoin: (code: string): Promise<PartyState> => call('POST', `/party/${encodeURIComponent(code)}/join`, {}),
  partyLeave: (): Promise<PartyState> => call('POST', '/party/leave', {}),
  partyInvite: (userId: string): Promise<{ invited: boolean }> => call('POST', '/party/invite', { userId }),
  /** The leader takes everyone: to this park, else a park with room for all. All seated, or none. */
  partyGo: (code?: string): Promise<JoinTicket> => call('POST', '/party/go', code ? { code } : {}),
  invites: (): Promise<{ invites: Invite[] }> => call('GET', '/invites'),
  dismissInvite: (code: string): Promise<{ dismissed: boolean }> =>
    call('POST', `/invites/${encodeURIComponent(code)}/dismiss`, {}),
  /** Our server checks the purchase with Lagos Life before it gives a single coin. */
  claim: (session: string, purchaseId: string): Promise<Claimed> =>
    call('POST', '/purchases/claim', { session, purchaseId }),
  /** Our website's packs: the pay checkout to send the player to. */
  checkout: (productId: string): Promise<{ url: string; reference: string }> =>
    call('POST', '/shop/checkout', { productId }),
  /** Back from the checkout: our server asks pay before it gives a single coin. */
  websitePurchase: (reference: string): Promise<WebsitePurchase> => call('POST', '/purchases/website', { reference }),
};
